import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import LoadingIcon from "../../assets/LoadingIcon";
import PrimaryButton from "../../components/button/PrimaryButton";
import ConfirmDialog from "../../components/dialog/ConfirmDialog";
import {
  deleteSuperAdminTenantFirebaseConfig,
  getSuperAdminTenantFirebaseConfig,
  updateSuperAdminTenantFirebaseConfig,
} from "../../services/superadmin-tenants";

const emptyConfig = {
  projectId: "",
  secretReference: "",
  active: true,
  secretFilePresent: null,
};

const isSafeSecretReference = (reference) => {
  const value = reference.trim();
  return value !== "" && !value.startsWith("/") && !value.includes("\\") &&
    !value.split("/").includes("..");
};

const TenantFirebaseConfigEdit = () => {
  const { tenantCode } = useParams();
  const navigate = useNavigate();
  const [config, setConfig] = useState(emptyConfig);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadConfig = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await getSuperAdminTenantFirebaseConfig(tenantCode);
        setConfig({ ...emptyConfig, ...response });
      } catch (e) {
        if (e.status === 404) {
          setConfig(emptyConfig);
        } else {
          setError(e.message);
        }
      } finally {
        setLoading(false);
      }
    };
    loadConfig();
  }, [tenantCode]);

  const updateField = (key, value) =>
    setConfig((previous) => ({ ...previous, [key]: value }));

  const handleSave = async () => {
    const projectId = config.projectId.trim();
    const secretReference = config.secretReference.trim();
    if (!projectId) {
      setError("Firebase project ID is required.");
      return;
    }
    if (!isSafeSecretReference(secretReference)) {
      setError("Secret reference must be a relative path without path traversal.");
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const response = await updateSuperAdminTenantFirebaseConfig(tenantCode, {
        projectId,
        secretReference,
        active: config.active,
      });
      setConfig({ ...emptyConfig, ...response });
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    setError(null);
    try {
      await deleteSuperAdminTenantFirebaseConfig(tenantCode);
      navigate(`/su/admin/tenants/${tenantCode}`, { replace: true });
    } catch (e) {
      setError(e.message);
      setShowDeleteConfirmation(false);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <LoadingIcon />;

  const hasExistingConfig = config.secretFilePresent !== null;
  return (
    <div className="bg-white rounded shadow p-6">
      <h1 className="uppercase text-xl font-bold text-slate-700 mb-2">
        Firebase Configuration
      </h1>
      <p className="text-sm text-slate-500 mb-6">
        Service-account files are provisioned on the server. Private key contents are never uploaded, displayed, or stored in this admin panel.
      </p>
      {error ? (
        <div className="bg-errorBg text-errorText text-sm p-4 mb-6 border-errorBorder border-[0.5px] rounded-sm">
          {error}
        </div>
      ) : null}
      <div className="mb-6 rounded bg-neutral-light p-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="uppercase text-sm font-bold text-slate-600">Credential readiness</h2>
            <p className="text-sm text-slate-600 mt-1">
              {config.secretFilePresent === null
                ? "No Firebase configuration has been saved for this tenant."
                : config.secretFilePresent
                  ? "The referenced credential file is readable by the backend."
                  : "The referenced credential file is not readable by the backend."}
            </p>
          </div>
          {config.secretFilePresent === null ? null : (
            <span className={`rounded px-3 py-1 text-xs font-bold uppercase ${config.secretFilePresent ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
              {config.secretFilePresent ? "Ready" : "Not ready"}
            </span>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block uppercase text-slate-600 text-xs font-bold mb-2" htmlFor="firebase-project-id">Firebase Project ID</label>
          <input id="firebase-project-id" aria-label="Firebase Project ID" type="text" className="border-0 px-3 py-3 text-slate-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full" value={config.projectId} onChange={(event) => updateField("projectId", event.target.value)} />
        </div>
        <div>
          <label className="block uppercase text-slate-600 text-xs font-bold mb-2" htmlFor="firebase-secret-reference">Service-account Secret Reference</label>
          <input id="firebase-secret-reference" aria-label="Service-account Secret Reference" type="text" className="border-0 px-3 py-3 text-slate-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full" value={config.secretReference} onChange={(event) => updateField("secretReference", event.target.value)} />
          <p className="text-xs text-slate-500 mt-2">Use a relative path beneath the backend Firebase secret root.</p>
        </div>
      </div>
      <label className="mt-6 inline-flex items-center gap-3 text-sm text-slate-700">
        <input aria-label="Firebase delivery enabled" type="checkbox" checked={config.active} onChange={(event) => updateField("active", event.target.checked)} />
        Enable Firebase delivery for this tenant
      </label>
      <div className="flex flex-wrap justify-between gap-3 mt-8">
        <div>
          {hasExistingConfig ? <button type="button" className="text-red-600 font-bold uppercase text-sm" onClick={() => setShowDeleteConfirmation(true)} disabled={deleting}>Remove Firebase Configuration</button> : null}
        </div>
        <PrimaryButton title="Save Firebase Configuration" onClick={handleSave} isBusy={saving} />
      </div>
      <ConfirmDialog title="Remove Firebase Configuration?" visible={showDeleteConfirmation} onAccept={handleDelete} onDismiss={() => setShowDeleteConfirmation(false)}>
        Removing this configuration stops Firebase delivery for this tenant. The service-account file remains on the server.
      </ConfirmDialog>
    </div>
  );
};

export default TenantFirebaseConfigEdit;
